import type { Doc, Note } from '../types'

/**
 * Flattens the document itself rather than reading the denormalised `text`
 * field: that field is derived on save and can be empty or stale, and sharing
 * an empty note because of a stale cache would be worse than useless.
 */
function flatten(node: unknown, out: string[] = [], line: string[] = []): string[] {
  const n = node as { type?: string; text?: string; content?: unknown[] }
  if (!n || typeof n !== 'object') return out
  if (n.type === 'text') { line.push(n.text ?? ''); return out }

  const isBlock = n.type !== undefined && n.type !== 'doc' && !['text'].includes(n.type)
  const own: string[] = isBlock ? [] : line
  for (const child of n.content ?? []) flatten(child, out, own)
  if (isBlock && own.length) out.push(own.join(''))
  return out
}

export function docText(doc: Doc): string {
  return flatten(doc).filter((l) => l.trim()).join('\n')
}

/** What a shared note looks like as plain text. */
export function shareText(note: Note): string {
  const body = docText(note.content).trim()
  const title = (note.title || 'New Note').trim()
  // The title is the note's first line, so repeating it would duplicate it.
  return body.startsWith(title) ? body : `${title}\n\n${body}`
}

export type ShareOutcome = 'shared' | 'copied' | 'cancelled' | 'failed'

/**
 * Hands a note to the system share sheet where there is one, and falls back to
 * the clipboard everywhere else.
 *
 * Android and iOS get the real sheet — mail, messages, whatever is installed.
 * A desktop browser usually has no share target, and a copy is the useful
 * thing there rather than an error.
 */
export async function shareNote(note: Note, nav: Navigator = navigator): Promise<ShareOutcome> {
  const text = shareText(note)
  const title = note.title || 'New Note'

  if (typeof nav.share === 'function') {
    try {
      await nav.share({ title, text })
      return 'shared'
    } catch (err) {
      // Dismissing the sheet rejects with AbortError; that is not a failure.
      if ((err as Error)?.name === 'AbortError') return 'cancelled'
      // Anything else falls through to the clipboard rather than dead-ending.
    }
  }

  try {
    await nav.clipboard.writeText(text)
    return 'copied'
  } catch {
    return 'failed'
  }
}
